import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  Typography,
  Paper,
  IconButton,
  TextField,
  CircularProgress,
  FormHelperText,
  Tooltip,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
} from "@mui/material";
import * as Yup from "yup";
import CloseIcon from "@mui/icons-material/Close";
import { Form, Formik, useFormikContext } from "formik";
import MasterDataAutoComplete from "../../pages/NewDesign/MasterDataAutoComplete";
import { useMutation } from "@tanstack/react-query";
import { generateJobDescription } from "../../api/agent";
import { usePlaygroundContext } from "../../pages/Playground";
import MessagePopup from "../../layout/messagePopup";

const inputStyles = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#050d2e",
    "& fieldset": {
      borderColor: "#050d2e",
    },
    "&:hover fieldset": {
      borderColor: "#000",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#050d2e",
    },
  },
  "& input": {
    fontSize: "14px",
    color: "#fff",
  },
  // ✅ Correct way to style the placeholder text
  "& input::placeholder": {
    color: "#b0b7c3 !important",
    opacity: 1, // Ensure it is fully visible
  },
};
export interface IGenerateJdModel {
  eximiusID?: string;
  id?: string;
  jobId?: string;
  industry?: string;
  industries: string[];
  jobCategory?: string;
  jobTitle: string;
  jobDescription?: string;
  jobDescriptionExt?: string;
  domain?: string;
  numberOfOpenings?: number;
  jobType?: string;
  taxTerms?: string;
  jobDuration?: number;
  jobAssignmentType?: string;
  billRate?: string;
  packageCurrency?: string;
  city?: string;
  state?: string;
  country?: string;
  zipCode?: string;
  jobExperience?: string;
  workingHours?: number;
  shiftTiming?: string;
  jobWorkAuthorization?: string[];
  jobStartDate?: string;
  skills?: string[];
  tools?: string[];
  technology?: string[];
  education?: string[];
  certificationLicences?: string[];
  skillsExt?: string[];
  toolsExt?: string[];
  technologyExt?: string[];
  educationExt?: string[];
  certificationLicencesExt?: string[];
  shiftTimingTo?: string;
  shiftTimingFrom?: string;
  salaryRange?: string;
  salaryRangeCurrencyType?: string;
  paymentMode?: string;
  primarySkill?: string;
  jobRole?: string;
  salePerson?: string;
  clientName?: string;
}

interface JobDescriptionFormProps {
  setGeneratedJob: any;
  generatedJob: any;
  onPrevious: () => void;
}

const JobDescriptionForm: React.FC<JobDescriptionFormProps> = ({
  setGeneratedJob,
  generatedJob,
  onPrevious,
}) => {
  const playgroundContext = usePlaygroundContext();
  if (!playgroundContext) {
    throw new Error("PlaygroundContext is not available");
  }
  const { playground, setPlayground } = playgroundContext;
  const [loadingPopupOpen, setLoadingPopupOpen] = useState(false);
  const { mutate } = useMutation({
    mutationFn: generateJobDescription,
    onSuccess: (data: any) => {
      // First clear previous job data
      resetPreviousJobData();
      
      // Then set the new data
      setPlayground((prev) => {
        return { ...prev, skills: skills };
      });
    
      if (data?.status_code === 200) {
        setPlayground((prev) => ({
          ...prev,
          jobDescription: data,
          userJobDescription: jobDescription,
          page: 4,
          jobTitle: jobTitle,
          skills: skills,
          // Clear candidate search data
          candidates: [],
          education: [],
          certifications: [],
          startDate: null,
        }));
        setGeneratedJob(data);
      } else {
        setError({
          show: true,
          errorMessage: "Something went wrong, try again",
          type: "error",
        });
      }
      setIsLoading(false);
      setLoadingPopupOpen(false);
    },
    onError: () => {
      setError({
        show: true,
        errorMessage: "Something went wrong, try again",
        type: "error",
      });
      setIsLoading(false);
      setLoadingPopupOpen(false);
    },
  });

  const [jobTitle, setJobTitle] = useState(playground.jobTitle ?? "");
  const [jobDescription, setJobDescription] = useState(
    playground.userJobDescription ?? ""
  );
  const [skills, setSkills] = useState<string[]>(playground.skills || []);
  const [inputSkill, setInputSkill] = useState("");
  const context = useFormikContext<IGenerateJdModel>();
  const [focusedField, setFocusedField] = useState<
    "jobTitle" | "jobDescription" | "skills" | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<
    | {
        show: boolean;
        errorMessage: string;
        type?: "error" | "info" | "success" | "warning";
      }
    | undefined
  >();

  const handleAddSkill = (skill: string) => {
    const trimmedSkill = skill.trim();
    if (trimmedSkill && !skills.includes(trimmedSkill)) {
      setSkills([...skills, trimmedSkill]);
    }
  };

  const handleDeleteSkill = (skillToDelete: string) => {
    setSkills(skills.filter((skill) => skill !== skillToDelete));
  };
  const handlePrevious = () => {
    // Clear all relevant state when navigating back
    setSkills([]);
    setJobTitle("");
    setJobDescription("");
    
    // Reset the playground context properly
    setPlayground((prev) => ({
      ...prev,
      skills: [],
      jobTitle: "",
      userJobDescription: "",
      // Optionally clear other relevant fields:
      jobDescription: null,
      education: [],
      certifications: [],
      workAuthorization: {
        citizen: false,
        permanentResident: false,
        other: false
      }
    }));
    
    onPrevious();
  };

  const resetPreviousJobData = () => {
    // Reset previous job-related data in the playground context
    setPlayground((prev) => ({
      ...prev,
      education: [],
      certifications: [],
      city: "",
      state: "",
      payrate: "",
      experience: 0,
      candidates: [],
      startDate: null,
    }));
  };

  const validateJobDescription = (value: string) => {
    if (!value) {
      return "Please provide a job description";
    }
    if (value.length < 30) {
      return "Please provide a job description with a minimum of 30 characters";
    }
    return "";
  };

  const onGenerateJD = async (
    jobTitle: string = "",
    jobDescription: string = "",
    skills: string[] = []
  ) => {
    if (!jobTitle) {
      setError({
        show: true,
        errorMessage: "Job Title is required",
        type: "error",
      });
      return;
    }
    const jobDescriptionError = validateJobDescription(jobDescription);
    if (jobDescriptionError) {
      setError({
        show: true,
        errorMessage: jobDescriptionError,
        type: "error",
      });
      return;
    }
    setIsLoading(true);
    setLoadingPopupOpen(true);

    if (!context || !context.values) {
      setError({
        show: true,
        errorMessage:
          "Formik context is not available. Please ensure that Formik is set up correctly.",
        type: "error",
      });
      return;
    }

    const payload = {
      jobTitle,
      clientName: "Compunnel",

      jobDescription: jobDescription,
      certificationLicences: [
        (context.values?.certificationLicences ?? []),
        (context.values?.certificationLicencesExt ?? []),
      ],
      skills: [
        (context.values?.skills ?? []),
        (context.values?.skillsExt ?? []),
        skills,
      ],
      tools: [
        (context.values?.tools ?? []),
        (context.values?.toolsExt ?? []),
      ],
      technology: [
        (context.values?.technology ?? []),
        (context.values?.technologyExt ?? []),
      ],
      education: [
        (context.values?.education ?? []),
        (context.values?.educationExt ?? []),
      ],
    };

    mutate(payload);
  };
  const scrollToTop = () => {
    window.scroll(0, 0);
  };

  useEffect(() => {
    scrollToTop();
  }, []);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box p={isMobile ? 2 : 4} pt={0}>
      <Grid
        container
        spacing={2}
        sx={{
          minHeight: "100vh",
          background: "transparent",
          color: "white",
          py: {
            xs: 2, // For extra small screens
            md: 2, // For medium screens (default)
          },
          px: { xs: 2, md: 6, lg: 12 },
        }}
        direction="column"
        alignItems="stretch"
      >
        <>
          <Grid item xs={12} sx={{ mb: isMobile ? 5 : 5 }}>
            <Typography
              variant="h6"
              className="page-title"
              sx={{ marginBottom: "3px" }}
            >
              Generate Job Description
            </Typography>
            <Typography sx={{ mb: -4 }}>
              Enter the job title, provide a brief overview, list any required
              skills, and let Eximius Al instantly create a tailored,
              professional job description based on your inputs.
              <br />
              <Typography>
                Tip: For an enhanced experience, use job titles from the IT and
                Healthcare domains. Once integrated with your systems and job
                boards, Eximius Al will retrieve all relevant candidates.{" "}
              </Typography>
            </Typography>
          </Grid>
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <Formik
                initialValues={{
                  jobTitle: playground.jobTitle,
                  jobDescription: playground.userJobDescription,
                }}
                validationSchema={Yup.object({
                  jobTitle: Yup.string().required("Job Title is required"),
                  jobDescription: Yup.string()
                    .required("Job Description is required")
                    .min(30, "Job Description must be at least 30 characters"),
                })}
                onSubmit={(values, { setSubmitting }) => {
                  setJobTitle(values.jobTitle ?? "");
                  setJobDescription(values.jobDescription ?? "");
                  onGenerateJD(values.jobTitle, values.jobDescription, skills);
                  setSubmitting(false);
                }}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  setFieldValue,
                  resetForm,
                }) => (
                  <Box
                    sx={{
                      pl: { xs: 1, md: 3 },
                      height: "94%",
                      backgroundColor: "transparent",
                      // "& .MuiInputBase-input::placeholder": {
                      //   color: "white",
                      //   opacity: 0.3
                      // }
                    }}
                  >
                    <Form>
                      <Grid container spacing={2} mb={3}>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ mb: 3 }}>
                            <MasterDataAutoComplete
                              label="Job Title*"
                              type="jobTitle"
                              selectedValue={values.jobTitle ?? ""}
                              setSelectedValue={(value) =>
                                setFieldValue("jobTitle", value || "")
                              }
                              onBlur={handleBlur("jobTitle")}
                              multiple={false}
                            />
                            {touched.jobTitle && errors.jobTitle && (
                              <FormHelperText error sx={{ mt: 1 }}>
                                {errors.jobTitle}
                              </FormHelperText>
                            )}
                            <Typography
                              sx={{
                                fontSize: isMobile ? "10px" : "12px",
                                color: "rgba(207, 209, 216, 1)",
                              }}
                            >
                              e.g., "Registered Nurse - ICU", "Phlebotomist",
                              "Java Developer", "Business Analyst", "Forklift
                              Operator", etc.
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box
                            sx={{
                              mb: 3,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              position: "relative",
                              width: "100%",
                              maxWidth: "500px",
                            }}
                          >
                            <TextField
                              className="field-textbox"
                              fullWidth
                              placeholder="Type a skill and press Enter or Comma"
                              variant="outlined"
                              size="small"
                              value={inputSkill}
                              onChange={(e) => {
                                setInputSkill(e.target.value);
                                if (e.target.value.includes(",")) {
                                  e.preventDefault();
                                  setSkills([...skills, inputSkill.trim()]);
                                  setInputSkill("");
                                }
                              }}
                              onKeyDown={(e) => {
                                if (
                                  (e.key === "Enter" || e.key === ",") &&
                                  inputSkill.trim()
                                ) {
                                  e.preventDefault();
                                  setSkills([...skills, inputSkill.trim()]);
                                  setInputSkill("");
                                  const inputElement =
                                    e.target as HTMLInputElement;
                                  inputElement.focus(); // Now this will not throw an error
                                }
                              }}
                              onBlur={() => {
                                if (inputSkill.trim()) {
                                  setSkills([...skills, inputSkill.trim()]);
                                  setInputSkill("");
                                }
                                setFocusedField(null);
                              }}
                              sx={{
                                ...inputStyles,
                                height: "42px",
                                minHeight: "0px",
                                "& .MuiOutlinedInput-root": {
                                  height: "42px",
                                  padding: 0,
                                  "&:hover fieldset": {
                                    borderColor: "transparent",
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: "transparent",
                                  },
                                },
                                //           "& .MuiInputBase-input::placeholder": {
                                //   color: "white", // Change this to your desired color
                                //   opacity: 0.3, // Optional: To ensure full opacity
                                // },
                                "& input": {
                                  fontSize: "14px",
                                  color: "#fff",
                                  lineHeight: "20px",
                                },
                              }}
                              onFocus={() => setFocusedField("skills")}
                            />
                            <Typography
                              sx={{
                                fontSize: isMobile ? "10px" : "12px",
                                color: "rgba(207, 209, 216, 1)",
                              }}
                            >
                              List required skills (e.g., React.js, TypeScript,
                              Team Leadership, etc.). Press Enter or Comma to
                              add.
                            </Typography>

                            {/* Render Skill Chips Below the Input Field */}
                            <Box
                              display="flex"
                              gap={1}
                              flexWrap="wrap"
                              sx={{
                                marginTop: 1,
                                width: "100%",
                              }}
                            >
                              {skills.map((skill) => {
                                const isLongSkill = skill.length > 15;

                                const chipComponent = (
                                  <Chip
                                    key={skill}
                                    label={skill}
                                    sx={{
                                      bgcolor: "#3b4c90",
                                      color: "#fff",
                                      fontSize: "12px",
                                      maxWidth: "130px",
                                      "& .MuiChip-label": {
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      },
                                    }}
                                    onDelete={(e) => {
                                      e.stopPropagation();
                                      setSkills(
                                        skills.filter((s) => s !== skill)
                                      );
                                    }}
                                    deleteIcon={
                                      <CloseIcon
                                        fontSize="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                        }}
                                      />
                                    }
                                  />
                                );

                                return isLongSkill ? (
                                  <Tooltip
                                    key={`tooltip-${skill}`}
                                    title={skill}
                                    arrow
                                    placement="top"
                                    componentsProps={{
                                      tooltip: {
                                        sx: {
                                          bgcolor: "rgba(0, 0, 0, 0.8)",
                                          "& .MuiTooltip-arrow": {
                                            color: "rgba(0, 0, 0, 0.8)",
                                          },
                                        },
                                      },
                                    }}
                                  >
                                    {chipComponent}
                                  </Tooltip>
                                ) : (
                                  chipComponent
                                );
                              })}
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>

                      <Box sx={{ mb: 3 }}>
                        <TextField
                          className="field-border"
                          fullWidth
                          placeholder="Enter job description*"
                          variant="outlined"
                          multiline
                          rows={6}
                          name="jobDescription"
                          value={values.jobDescription}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          inputProps={{ maxLength: 5000 }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "rgba(5, 13, 46, 1) !important;",
                              "& fieldset": {
                                borderColor: "#050d2e",
                              },
                              "&:hover fieldset": {
                                borderColor: "#000",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#050d2e",
                              },
                            },
                            "& .MuiInputBase-input": {
                              fontSize: "14px",
                              color: "#fff",
                            },
                            "& textarea::placeholder": {
                              color: "#b0b7c3 !important",
                              opacity: 1, // Ensure it's fully visible
                            },
                          }}
                          onFocus={() => setFocusedField("jobDescription")}
                        />

                        {touched.jobDescription && errors.jobDescription && (
                          <FormHelperText error sx={{ mt: 1 }}>
                            {errors.jobDescription ?? ""}
                          </FormHelperText>
                        )}

                        <Typography
                          fontSize={isMobile ? "10px" : "12px"}
                          color={"rgba(207, 209, 216, 1) "}
                        >
                          e.g., "Compassionate and skilled registered Nurse for
                          a large hospital brand to provide critical care and
                          support in the ICU"
                        </Typography>
                      </Box>

                      <Box display={isMobile ? "block" : "flex"} gap={2}>
                        {isMobile && (
                          <Button
                            type="submit"
                            variant="contained"
                            sx={{
                              marginTop: isMobile ? 2 : 0,
                              marginBottom: 2,
                              textTransform: "none",
                              height: "44px",
                              color: "#fff",
                              bgcolor: "#007BFF",
                              borderRadius: "45px",
                              padding: "12px 20px",
                              fontSize: isMobile ? "11px" : "16px",
                              fontWeight: "bold",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "10px",
                              "&:hover": {
                                bgcolor: "#0052CC",
                              },
                              "&:disabled": {
                                bgcolor: "#0052CC",
                                color: "#fff",
                              },
                            }}
                            disabled={isLoading}
                            endIcon={
                              isLoading && (
                                <CircularProgress
                                  size={24}
                                  sx={{ color: "#fff" }}
                                />
                              )
                            }
                            fullWidth={isMobile ? true : false}
                          >
                            Generate Job Description
                          </Button>
                        )}

                        <Button
                          onClick={handlePrevious}
                          variant="contained"
                          sx={{
                            textTransform: "none",
                            height: "44px",
                            color: "#fff",
                            bgcolor: "#7a7a7a",
                            borderRadius: "45px",
                            padding: "12px 20px",
                            fontSize: isMobile ? "11px" : "16px",
                            fontWeight: "bold",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "10px",
                          }}
                          fullWidth={isMobile ? true : false}
                        >
                          Previous
                        </Button>

                        {!isMobile && (
                          <Button
                            type="submit"
                            variant="contained"
                            sx={{
                              marginTop: isMobile ? 2 : 0,
                              textTransform: "none",
                              height: "44px",
                              color: "#fff",
                              bgcolor: "#007BFF",
                              borderRadius: "45px",
                              padding: "12px 20px",
                              fontSize: isMobile ? "11px" : "16px",
                              fontWeight: "bold",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "10px",
                              "&:hover": {
                                bgcolor: "#0052CC",
                              },
                              "&:disabled": {
                                bgcolor: "#0052CC",
                                color: "#fff",
                              },
                            }}
                            disabled={isLoading}
                            endIcon={
                              isLoading && (
                                <CircularProgress
                                  size={24}
                                  sx={{ color: "#fff" }}
                                />
                              )
                            }
                            fullWidth={isMobile ? true : false}
                          >
                            Generate Job Description
                          </Button>
                        )}
                      </Box>
                    </Form>
                  </Box>
                )}
              </Formik>
            </Grid>
          </Grid>
        </>
      </Grid>
      <MessagePopup
        open={loadingPopupOpen}
        message="Our AI Engine is crafting a detailed job description based on your inputs. Please wait..."
      />
    </Box>
  );
};

export default JobDescriptionForm;
